import React, { useState, useEffect } from 'react';
import { TonConnectButton, useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { useTranslation } from 'react-i18next';
import api, { gameAPI } from '../services/api';
import { useStore } from '../store/useStore';
import { formatNumber } from '../utils/formatters';
import './WalletPage.css';

interface Transaction {
  id: number;
  type: 'deposit' | 'withdrawal' | 'exchange';
  amount: number;
  currency: 'TON' | 'SPERM';
  status: 'pending' | 'completed' | 'failed';
  hash?: string;
  created_at: string;
}

const WalletPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, updateBalance } = useStore();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();

  const [activeTab, setActiveTab] = useState<'deposit' | 'withdraw' | 'exchange' | 'history'>('deposit');
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  // Состояние для депозита
  const [depositAmount, setDepositAmount] = useState('');
  const [depositCurrency, setDepositCurrency] = useState<'TON' | 'SPERM'>('TON');

  // Состояние для вывода
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawCurrency, setWithdrawCurrency] = useState<'TON' | 'SPERM'>('SPERM');
  const [withdrawAddress, setWithdrawAddress] = useState('');

  // 🔐 2FA состояния
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  // Состояние для обмена
  const [exchangeAmount, setExchangeAmount] = useState('');
  const [exchangeFrom, setExchangeFrom] = useState<'TON' | 'SPERM'>('TON');
  const [exchangeTo, setExchangeTo] = useState<'TON' | 'SPERM'>('SPERM');
  const [exchangeRate, setExchangeRate] = useState({ tonToSperm: 89, spermToTon: 0.011 });
  
  // Балансы
  const [balances, setBalances] = useState({
    ton: 0,
    sperm: user?.balance || 0
  });

  // 🔒 Anti-fraud: Доступное к выводу
  const [withdrawLimits, setWithdrawLimits] = useState({
    availableToWithdraw: 0,
    needToWager: 0,
    totalWagered: 0,
    totalSlotWinnings: 0
  });

  // 🚫 ЧС выводов - полная блокировка
  const [withdrawalBlocked, setWithdrawalBlocked] = useState(false);

  useEffect(() => {
    if (address) {
      loadWalletData();
    }
    loadExchangeRate();
    loadTransactions();
    loadWithdrawLimits();
    checkWithdrawalBlacklist();
  }, [address]);

  // 🚫 Проверка статуса ЧС выводов
  const checkWithdrawalBlacklist = async () => {
    try {
      const response = await api.get('/wallet/blacklist-status');
      if (response.data.success) {
        setWithdrawalBlocked(response.data.blacklisted || false);
      }
    } catch (error) {
      console.error('Ошибка проверки статуса ЧС:', error);
    }
  };

  // 🔐 Таймер обратного отсчёта для повторной отправки кода
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 🔒 Загрузка лимитов на вывод
  const loadWithdrawLimits = async () => {
    try {
      const response = await api.get('/wallet/available-withdraw');
      if (response.data.success && response.data.data) {
        setWithdrawLimits({
          availableToWithdraw: response.data.data.availableToWithdraw || 0,
          needToWager: response.data.data.needToWager || 0,
          totalWagered: response.data.data.totalWagered || 0,
          totalSlotWinnings: response.data.data.totalSlotWinnings || 0
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки лимитов вывода:', error);
    }
  };

  const loadWalletData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/wallet/transactions');
      
      if (response.data.success) {
        setBalances(response.data.balances);
      }
    } catch (error) {
      console.error(t('walletPage.errorLoadBalance'), error);
    }
  };

  const loadExchangeRate = async () => {
    try {
      const response = await api.get('/wallet/info');
      if (response.data.success) {
        setExchangeRate(response.data.rate);
      }
    } catch (error) {
      console.error(t('walletPage.errorLoadRate'), error);
    }
  };

  const loadTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/wallet/transactions');
      
      if (response.data.success) {
        setTransactions(response.data.data?.transactions || response.data.transactions || []);
      }
    } catch (error) {
      console.error(t('walletPage.errorLoadTransactions'), error);
    }
  };

  const handleDeposit = async () => {
    if (!address) {
      alert(t('walletPage.alertConnectWallet'));
      return;
    }

    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      alert(t('walletPage.alertInvalidAmount'));
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/wallet/deposit',
        {
          amount: parseFloat(depositAmount),
          currency: depositCurrency,
          walletAddress: address
        }
      );
      
      if (response.data.success) {
        alert(t('walletPage.alertDepositCreated'));
        setDepositAmount('');
        loadTransactions();
      }
    } catch (error) {
      console.error(t('walletPage.errorCreateDeposit'), error);
      alert(t('walletPage.alertDepositError'));
    } finally {
      setLoading(false);
    }
  };

  // 🔐 Шаг 1: Открыть модалку 2FA
  const handleWithdraw = async () => {
    // Проверяем подключение кошелька
    if (!address) {
      alert(t('walletPage.alertConnectWallet'));
      return;
    }

    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      alert(t('walletPage.alertInvalidAmount'));
      return;
    }

    if (!withdrawAddress) {
      alert(t('walletPage.alertEnterAddress'));
      return;
    }

    // Открываем модалку 2FA
    setCodeError('');
    setVerificationCode('');
    setCodeSent(false);
    setShow2FAModal(true);
  };

  // 🔐 Шаг 2: Запросить код в Telegram
  const requestWithdrawalCode = async () => {
    setLoading(true);
    setCodeError('');

    try {
      const response = await api.post('/wallet/request-withdrawal-code', {
        amount: parseFloat(withdrawAmount),
        toAddress: withdrawAddress,
        currency: withdrawCurrency
      });

      if (response.data.success) {
        setCodeSent(true);
        setCountdown(60); // 60 секунд до повторной отправки
      }
    } catch (error: any) {
      console.error('Ошибка запроса кода:', error);
      setCodeError(error.response?.data?.message || error.response?.data?.error || 'Ошибка отправки кода');
    } finally {
      setLoading(false);
    }
  };

  // 🔐 Шаг 3: Подтвердить вывод с кодом
  const confirmWithdrawal = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setCodeError('Введите 6-значный код');
      return;
    }

    setLoading(true);
    setCodeError('');

    try {
      const response = await api.post('/wallet/withdraw', {
        amount: parseFloat(withdrawAmount),
        toAddress: withdrawAddress,
        currency: withdrawCurrency,
        code: verificationCode
      });

      if (response.data.success) {
        setShow2FAModal(false);
        setWithdrawAmount('');
        setWithdrawAddress('');
        setVerificationCode('');
        alert(t('walletPage.alertWithdrawCreated'));
        loadTransactions();
        loadWithdrawLimits();
        if (user) {
          updateBalance(user.balance - parseFloat(withdrawAmount));
        }
      }
    } catch (error: any) {
      console.error('Ошибка вывода:', error);
      setCodeError(error.response?.data?.message || error.response?.data?.error || 'Неверный код или ошибка вывода');
    } finally {
      setLoading(false);
    }
  };

  const handleExchange = async () => {
    if (!exchangeAmount || parseFloat(exchangeAmount) <= 0) {
      alert(t('walletPage.alertInvalidAmount'));
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(
        '/exchange/swap',
        {
          amount: parseFloat(exchangeAmount),
          from: exchangeFrom,
          to: exchangeTo
        },
        {
          }
      );
      
      if (response.data.success) {
        alert(t('walletPage.alertExchangeSuccess'));
        setExchangeAmount('');
        loadWalletData();
        loadTransactions();
      }
    } catch (error: any) {
      console.error(t('walletPage.errorExchange'), error);
      alert(error.response?.data?.error || t('walletPage.alertExchangeError'));
    } finally {
      setLoading(false);
    }
  };

  const calculateExchangeResult = () => {
    if (!exchangeAmount || parseFloat(exchangeAmount) <= 0) return 0;
    
    const amount = parseFloat(exchangeAmount);
    if (exchangeFrom === 'TON' && exchangeTo === 'SPERM') {
      return amount * exchangeRate.tonToSperm;
    } else {
      return amount * exchangeRate.spermToTon;
    }
  };

  return (
    <div className="wallet-page">
      <div className="wallet-header">
        <h2>{t('walletPage.title')}</h2>
        <div className="wallet-connect">
          <TonConnectButton />
        </div>
      </div>

      {/* Балансы */}
      <div className="balances-grid">
        <div className="balance-card ton-balance">
          <div className="balance-icon">💎</div>
          <div className="balance-info">
            <span className="balance-label">TON</span>
            <span className="balance-amount">{formatNumber(balances.ton, 4)}</span>
            <span className="balance-usd">≈ ${(balances.ton * 3.2).toFixed(2)}</span>
          </div>
        </div>
        
        <div className="balance-card sperm-balance">
          <div className="balance-icon">🪙</div>
          <div className="balance-info">
            <span className="balance-label">SPERM</span>
            <span className="balance-amount">{formatNumber(balances.sperm, 0)}</span>
            <span className="balance-usd">≈ ${(balances.sperm * 0.036).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Табы */}
      <div className="wallet-tabs">
        <button
          className={`tab-button ${activeTab === 'deposit' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposit')}
        >
          {t('walletPage.tabDeposit')}
        </button>
        <button
          className={`tab-button ${activeTab === 'withdraw' ? 'active' : ''}`}
          onClick={() => setActiveTab('withdraw')}
        >
          {t('walletPage.tabWithdraw')}
        </button>
        <button
          className={`tab-button ${activeTab === 'exchange' ? 'active' : ''}`}
          onClick={() => setActiveTab('exchange')}
        >
          {t('walletPage.tabExchange')}
        </button>
        <button
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          {t('walletPage.tabHistory')}
        </button>
      </div>

      {/* Контент табов */}
      <div className="wallet-content">
        {/* Депозит */}
        {activeTab === 'deposit' && (
          <div className="deposit-section">
            <h3>{t('walletPage.depositTitle')}</h3>

            {!address ? (
              <div className="connect-prompt">
                <p>{t('walletPage.depositConnectPrompt')}</p>
                <TonConnectButton />
              </div>
            ) : (
              <>
                <div className="currency-selector">
                  <button 
                    className={`currency-btn ${depositCurrency === 'TON' ? 'active' : ''}`}
                    onClick={() => setDepositCurrency('TON')}
                  >
                    TON
                  </button>
                  <button 
                    className={`currency-btn ${depositCurrency === 'SPERM' ? 'active' : ''}`}
                    onClick={() => setDepositCurrency('SPERM')}
                  >
                    SPERM
                  </button>
                </div>
                
                <div className="input-group">
                  <label>{t('walletPage.depositAmount')}</label>
                  <input
                    type="number"
                    placeholder={`${t('walletPage.depositMinimum')} ${depositCurrency === 'TON' ? '0.1' : '100'} ${depositCurrency}`}
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                  />
                </div>
                
                <div className="quick-amounts">
                  {depositCurrency === 'TON' ? 
                    ['0.5', '1', '5', '10'].map(amount => (
                      <button
                        key={amount}
                        className="quick-amount-btn"
                        onClick={() => setDepositAmount(amount)}
                      >
                        {amount} TON
                      </button>
                    )) :
                    ['100', '500', '1000', '5000'].map(amount => (
                      <button
                        key={amount}
                        className="quick-amount-btn"
                        onClick={() => setDepositAmount(amount)}
                      >
                        {amount} SPERM
                      </button>
                    ))
                  }
                </div>
                
                <button
                  className="action-button deposit-btn"
                  onClick={handleDeposit}
                  disabled={loading}
                >
                  {loading ? t('walletPage.depositProcessing') : t('walletPage.depositCreate')}
                </button>
              </>
            )}
          </div>
        )}

        {/* Вывод */}
        {activeTab === 'withdraw' && (
          <div className="withdraw-section">
            <h3>{t('walletPage.withdrawTitle')}</h3>

            {/* 🚫 ЧС - полная блокировка вывода */}
            {withdrawalBlocked ? (
              <div className="withdraw-blocked-message blacklisted">
                <div className="blocked-icon">🚫</div>
                <h4>{t('walletPage.withdrawBlacklistedTitle', 'Вывод недоступен')}</h4>
                <p>{t('walletPage.withdrawBlacklistedDesc', 'Функция вывода временно недоступна для вашего аккаунта.')}</p>
              </div>
            ) : (
              <>
                {/* 🔒 Показываем доступное к выводу */}
                <div className="withdraw-limits-info">
                  <div className="limit-row">
                    <span className="limit-label">{t('walletPage.withdrawBalanceLabel', 'Баланс:')}</span>
                    <span className="limit-value">{formatNumber(balances.sperm, 0)} SPERM</span>
                  </div>
                  <div className="limit-row highlight">
                    <span className="limit-label">{t('walletPage.withdrawAvailableLabel', 'Доступно к выводу:')}</span>
                    <span className="limit-value available">{formatNumber(withdrawLimits.availableToWithdraw, 2)} SPERM</span>
                  </div>
                  {withdrawLimits.needToWager > 0 && (
                    <div className="limit-row warning">
                      <span className="limit-label">{t('walletPage.withdrawNeedWager', 'Нужно прокрутить:')}</span>
                      <span className="limit-value need-wager">{formatNumber(withdrawLimits.needToWager, 2)} SPERM</span>
                    </div>
                  )}
                </div>

                {/* Если ничего нельзя вывести - показываем сообщение */}
                {withdrawLimits.availableToWithdraw <= 0 ? (
              <div className="withdraw-blocked-message">
                <div className="blocked-icon">🔒</div>
                <h4>{t('walletPage.withdrawBlockedTitle', 'Вывод недоступен')}</h4>
                <p>{t('walletPage.withdrawBlockedDesc', 'Для вывода средств необходимо использовать их в системе:')}</p>
                <ul>
                  <li>{t('walletPage.withdrawBlockedOption1', '• Открыть стейкинг (LUNAR или STAR)')}</li>
                  <li>{t('walletPage.withdrawBlockedOption2', '• Играть в слоты')}</li>
                  <li>{t('walletPage.withdrawBlockedOption3', '• Участвовать в мини-играх')}</li>
                </ul>
                <p className="blocked-hint">{t('walletPage.withdrawBlockedHint', 'Выигрыши в слотах доступны к выводу сразу!')}</p>
              </div>
            ) : (
              <>
                <div className="currency-selector">
                  <button
                    className={`currency-btn ${withdrawCurrency === 'SPERM' ? 'active' : ''}`}
                    onClick={() => setWithdrawCurrency('SPERM')}
                  >
                    SPERM
                  </button>
                  <button
                    className={`currency-btn ${withdrawCurrency === 'TON' ? 'active' : ''}`}
                    onClick={() => setWithdrawCurrency('TON')}
                    disabled
                  >
                    {t('walletPage.withdrawComingSoon')}
                  </button>
                </div>

                <div className="input-group">
                  <label>{t('walletPage.withdrawAvailable')} {formatNumber(withdrawLimits.availableToWithdraw, 2)} SPERM</label>
                  <input
                    type="number"
                    placeholder={t('walletPage.withdrawAmountPlaceholder')}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    max={withdrawLimits.availableToWithdraw}
                  />
                </div>

                <div className="quick-amounts">
                  {[25, 50, 75, 100].map(percent => {
                    const amount = Math.floor(withdrawLimits.availableToWithdraw * percent / 100);
                    return amount > 0 ? (
                      <button
                        key={percent}
                        className="quick-amount-btn"
                        onClick={() => setWithdrawAmount(amount.toString())}
                      >
                        {percent}%
                      </button>
                    ) : null;
                  })}
                </div>

                <div className="input-group">
                  <label>{t('walletPage.withdrawAddress')}</label>
                  <input
                    type="text"
                    placeholder={t('walletPage.withdrawAddressPlaceholder')}
                    value={withdrawAddress}
                    onChange={(e) => setWithdrawAddress(e.target.value)}
                  />
                </div>

                <div className="fee-info">
                  <span>{t('walletPage.withdrawNetworkFee')} 0.1 TON</span>
                  <span>{t('walletPage.withdrawServiceFee')} 1%</span>
                </div>

                <button
                  className="action-button withdraw-btn"
                  onClick={handleWithdraw}
                  disabled={loading || parseFloat(withdrawAmount) > withdrawLimits.availableToWithdraw}
                >
                  {loading ? t('walletPage.depositProcessing') : t('walletPage.withdrawButton')}
                </button>
              </>
            )}
              </>
            )}
          </div>
        )}

        {/* Обмен */}
        {activeTab === 'exchange' && (
          <div className="exchange-section">
            <h3>{t('walletPage.exchangeTitle')}</h3>
            
            <div className="exchange-rate-info">
              <span>1 TON = {exchangeRate.tonToSperm} SPERM</span>
              <span>1 SPERM = {exchangeRate.spermToTon.toFixed(6)} TON</span>
            </div>
            
            <div className="exchange-inputs">
              <div className="exchange-input">
                <label>{t('walletPage.exchangeYouGive')}</label>
                <div className="input-with-select">
                  <input
                    type="number"
                    placeholder="0.00"
                    value={exchangeAmount}
                    onChange={(e) => setExchangeAmount(e.target.value)}
                  />
                  <select 
                    value={exchangeFrom}
                    onChange={(e) => {
                      setExchangeFrom(e.target.value as 'TON' | 'SPERM');
                      setExchangeTo(e.target.value === 'TON' ? 'SPERM' : 'TON');
                    }}
                  >
                    <option value="TON">TON</option>
                    <option value="SPERM">SPERM</option>
                  </select>
                </div>
                <span className="balance-hint">
                  {t('walletPage.exchangeBalance')} {exchangeFrom === 'TON' ? balances.ton : balances.sperm}
                </span>
              </div>

              <div className="exchange-arrow">⬇️</div>

              <div className="exchange-input">
                <label>{t('walletPage.exchangeYouReceive')}</label>
                <div className="input-with-select">
                  <input
                    type="text"
                    value={calculateExchangeResult().toFixed(exchangeTo === 'TON' ? 6 : 2)}
                    readOnly
                  />
                  <select value={exchangeTo} disabled>
                    <option value="TON">TON</option>
                    <option value="SPERM">SPERM</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="fee-info">
              <span>{t('walletPage.exchangeStonFee')} 0.3%</span>
              <span>{t('walletPage.exchangeSlippage')} 1%</span>
            </div>

            <button
              className="action-button exchange-btn"
              onClick={handleExchange}
              disabled={loading || !address}
            >
              {!address ? t('walletPage.exchangeConnectWallet') : loading ? t('walletPage.exchangeProcessing') : t('walletPage.exchangeButton')}
            </button>
          </div>
        )}

        {/* История */}
        {activeTab === 'history' && (
          <div className="history-section">
            <h3>{t('walletPage.historyTitle')}</h3>

            {transactions.length === 0 ? (
              <div className="no-transactions">
                <p>{t('walletPage.historyEmpty')}</p>
              </div>
            ) : (
              <div className="transactions-list">
                {transactions.map((tx: any) => {
                  const txLabels: Record<string, { icon: string; label: string }> = {
                    deposit: { icon: '📥', label: t('walletPage.historyDeposit') },
                    withdrawal: { icon: '📤', label: t('walletPage.historyWithdraw') },
                    withdrawal_usdt: { icon: '📤', label: 'USDT Withdrawal' },
                    exchange: { icon: '🔄', label: t('walletPage.historyExchange') },
                    lunar_staking: { icon: '🌙', label: 'Lunar Staking' },
                    star_staking: { icon: '⭐', label: 'Star Staking' },
                    staking_reward: { icon: '💰', label: 'Staking Reward' },
                    claim: { icon: '💰', label: 'Claim' },
                    referral_staking: { icon: '🎁', label: 'Referral Bonus' },
                    referral_daily: { icon: '💸', label: 'Referral Daily' },
                    slots: { icon: '🎰', label: 'Slots' },
                    piggybank_deposit: { icon: '🐷', label: 'Piggybank Claim' },
                    piggybank_withdrawal: { icon: '🐷', label: 'Piggybank Withdraw' },
                    marathon_deposit: { icon: '🏁', label: 'Marathon Deposit' },
                    marathon_profit: { icon: '🏁', label: 'Marathon Profit' },
                    admin_deposit: { icon: '👑', label: 'Admin Credit' },
                    task_reward: { icon: '📋', label: 'Task Reward' },
                    adsgram_reward: { icon: '📺', label: 'Ads FREE' },
                  };
                  const info = txLabels[tx.type] || { icon: '💫', label: tx.type };
                  const isPositive = (tx.amount || 0) > 0;
                  return (
                  <div key={tx.id} className={`transaction-item ${tx.type}`}>
                    <div className="tx-icon">{info.icon}</div>
                    <div className="tx-info">
                      <span className="tx-type">{info.label}</span>
                      <span className="tx-date">
                        {new Date(tx.created_at).toLocaleDateString('ru-RU')}
                      </span>
                    </div>
                    <div className="tx-amount">
                      <span className={isPositive ? 'positive' : 'negative'}>
                        {isPositive ? '+' : ''}{formatNumber(tx.amount, 2)} SPERM
                      </span>
                      <span className={`tx-status ${tx.status}`}>
                        {tx.status === 'pending' ? t('walletPage.historyPending') :
                         tx.status === 'completed' ? t('walletPage.historyCompleted') : t('walletPage.historyCancelled')}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 🔐 Модалка 2FA для подтверждения вывода */}
      {show2FAModal && (
        <div className="modal-overlay" onClick={() => setShow2FAModal(false)}>
          <div className="modal-content twofa-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShow2FAModal(false)}>×</button>

            <div className="twofa-header">
              <div className="twofa-icon">🔐</div>
              <h3>Подтверждение вывода</h3>
            </div>

            <div className="twofa-info">
              <div className="twofa-row">
                <span>Сумма:</span>
                <strong>{withdrawAmount} {withdrawCurrency}</strong>
              </div>
              <div className="twofa-row">
                <span>Адрес:</span>
                <strong className="address-short">
                  {withdrawAddress.slice(0, 8)}...{withdrawAddress.slice(-6)}
                </strong>
              </div>
            </div>

            {!codeSent ? (
              <div className="twofa-step1">
                <p className="twofa-desc">
                  Для безопасности вывода мы отправим 6-значный код в Telegram бот.
                </p>
                <button
                  className="action-button send-code-btn"
                  onClick={requestWithdrawalCode}
                  disabled={loading}
                >
                  {loading ? 'Отправка...' : '📱 Отправить код в Telegram'}
                </button>
              </div>
            ) : (
              <div className="twofa-step2">
                <p className="twofa-desc success">
                  ✅ Код отправлен в Telegram! Проверьте бота @moonraid_game_bot
                </p>

                <div className="code-input-wrapper">
                  <input
                    type="text"
                    className="code-input"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                  />
                </div>

                {codeError && (
                  <div className="twofa-error">{codeError}</div>
                )}

                <button
                  className="action-button confirm-btn"
                  onClick={confirmWithdrawal}
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? 'Проверка...' : '✅ Подтвердить вывод'}
                </button>

                <button
                  className="resend-btn"
                  onClick={requestWithdrawalCode}
                  disabled={countdown > 0 || loading}
                >
                  {countdown > 0
                    ? `Отправить повторно (${countdown}с)`
                    : '🔄 Отправить код повторно'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;